import { useEffect, useRef } from "react";
import {
  Checkbox,
  MultiSelect,
  NumberField,
  RadioGroup,
  Radio,
  Select,
  TextArea,
  TextField,
} from "@core/components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { EligibilityWarning, SchemaOption } from "../types";

/**
 * The amber note that sits beside an answer — « Portail/Avertissement d'éligibilité ».
 *
 * ⚠️ **Never an error, and never a blocker.** US-E3-04's core rule is that no answer a candidat
 * gives may prevent them from submitting, and its notes say the wording IS the story: a warning that
 * reads as a verdict becomes a self-rejection, and turns away exactly the rural, less-credentialled
 * applicants the foundation names as a priority. So this uses the WARNING tone, never error, carries
 * no error styling on the field itself, and every message ends with the door still open.
 *
 * The copy is the server's — localized from the round's own catalogue — so it stays the foundation's
 * words rather than ours.
 */
function FieldWarning({ warning }: { warning: EligibilityWarning }) {
  return (
    <p
      role="status"
      className="flex items-start gap-xs rounded-lg border border-(--feedback-warning-ring) bg-(--feedback-warning-ring-outer) p-sm text-sm-regular text-(--text-primary)"
    >
      <span
        aria-hidden
        className="mt-0.5 shrink-0 text-(--feedback-warning-icon)"
      >
        <Icon name="alert" size={16} />
      </span>
      {warning.message}
    </p>
  );
}

interface DossierFieldProps {
  label: string;
  helpText: string;
  /** `DROPDOWN` | `RADIO` | `NUMBER` | `SHORT_TEXT` | `LONG_TEXT` | `URL`, from the round. */
  inputType: string;
  isRequired: boolean;
  options: SchemaOption[];
  value: unknown;
  onChange: (value: unknown) => void;
  /**
   * Fired the moment a value is worth persisting — on blur for text, immediately for a
   * complete-in-one-click control (radio/select/checkbox).
   *
   * ⚠️ Always carries the value being committed. It must NOT be re-derived by the caller from
   * whatever local state `onChange` last wrote — `setState` inside an immediate-commit branch
   * (radio/select/checkbox call `onChange` then `onCommit` in the same synchronous handler) has
   * not flushed yet, so a caller reading its own state back at that point gets the value from
   * BEFORE this click, one click behind. Verified against the running stack: selecting "MULTIPLE"
   * sent `{"mode":"SOLO"}` on the wire until this became explicit.
   */
  onCommit: (value: unknown) => void;
  isDisabled?: boolean;
  /** A hard input error from the server, keyed to this field. Distinct from a warning. */
  errorMessage?: string;
  warnings?: EligibilityWarning[];
  /**
   * `NUMBER` only. Every round-authored numeric field (ages, seat counts) is a whole number, so
   * the shared `NumberField` primitive defaults to zero decimal places — and until this prop
   * existed, `DossierField` had no way to ask for more. `capital_percentage` is the first caller
   * that needs one: it's a `DecimalField` server-side (2 places), and without this, typing "12.5"
   * silently saved "125" (the input dropped the separator rather than reading it) — the backend
   * then correctly rejected the mangled value as over 100%, so nothing corrupted, but no
   * fractional share could ever be saved. Omit for the existing whole-number behavior.
   */
  maxFractionDigits?: number;
}

/**
 * One answer control, rendered entirely from the round's frozen schema (US-E3-03).
 *
 * The applicant's dossier is configuration, not code: an administrator changes the questions and
 * this renders them without a deploy. So nothing here knows what `weekly_commitment_code` means —
 * only that it is a `DROPDOWN` with these options and this label.
 *
 * ## An unknown input type degrades, it does not throw
 *
 * The default branch is a text field. `D15` ratifies a closed set of answer kinds, but the backend
 * owns that list and has grown before — and a step that renders 27 of its 28 questions is
 * recoverable, while one that crashes is not. Same reasoning as the loose `input_type` schema.
 */
export function DossierField({
  label,
  helpText,
  inputType,
  isRequired,
  options,
  value,
  onChange,
  onCommit,
  isDisabled = false,
  errorMessage,
  warnings = [],
  maxFractionDigits,
}: DossierFieldProps) {
  const { t } = useTranslation("acquire");
  const text = typeof value === "string" ? value : "";

  /**
   * Live mirror of `value`, read inside `onBlur` instead of closing over the prop directly.
   *
   * ⚠️ Verified live: React Aria's `NumberField` does not rebind `onBlur` to this component's
   * latest render the way the other branches assumed — typing a new age and tabbing away sent the
   * ORIGINAL value, not the one just typed, even though the input's own displayed text was
   * already correct. A ref sidesteps whichever render this closure was actually bound from: refs
   * are a shared, mutable box, so `.current` is live no matter which render's `onBlur` fired.
   */
  const latestValue = useRef(value);
  useEffect(() => {
    latestValue.current = value;
  });

  /**
   * Options are rendered by their STRINGIFIED value and saved by their original one.
   *
   * `residency_confirmed` is the case that forces it: its options are JSON booleans, React Aria keys
   * are strings, and the server rejects `"true"` for a `BooleanField`. Round-tripping through this
   * lookup keeps the wire type intact instead of quietly turning a boolean into a string.
   */
  const keyOf = (option: SchemaOption) => String(option.value);
  const valueForKey = (key: string): unknown =>
    options.find((option) => keyOf(option) === key)?.value ?? key;
  /** What the control shows as selected — the current value, stringified to match the keys. */
  const selectedKey =
    value === undefined || value === null || value === "" ? "" : String(value);

  /**
   * What every branch below shares, lifted out once rather than repeated five times: the label,
   * the requiredness, the lock, the help copy, and the error. Only the value binding and the commit
   * timing differ per input kind — that is the actual reason each branch still exists separately.
   */
  const common = {
    label,
    isRequired,
    isDisabled,
    description: helpText || undefined,
    errorMessage,
  };

  const control = (() => {
    switch (inputType) {
      case "DROPDOWN":
        return (
          <Select
            {...common}
            options={options.map((option) => ({
              id: keyOf(option),
              label: option.label,
            }))}
            selectedKey={selectedKey === "" ? null : selectedKey}
            // ⚠️ A GENERIC prompt. The design writes one per field (« Choisir une durée »,
            // « Choisir une situation »), but that copy is per-question and not in the payload —
            // inventing 16 of them here would be a hand-mirror that goes stale the moment an
            // administrator edits a question. Without it the primitive echoes the label back as
            // its own placeholder, which reads as if the field were already answered.
            placeholder={t("dossier.select-placeholder")}
            // Selecting is a complete act, so it commits immediately — there is no "still typing"
            // state for a listbox to wait out.
            onSelectionChange={(key) => {
              const next = key === null ? "" : valueForKey(String(key));
              onChange(next);
              onCommit(next);
            }}
          />
        );

      case "RADIO":
        return (
          <RadioGroup
            {...common}
            value={selectedKey}
            onChange={(next) => {
              const resolved = valueForKey(next);
              onChange(resolved);
              onCommit(resolved);
            }}
          >
            {options.map((option) => (
              <Radio key={keyOf(option)} value={keyOf(option)}>
                {option.label}
              </Radio>
            ))}
          </RadioGroup>
        );

      // `named_members_consent_confirmed` is the first CHECKBOX-type field this dossier renders
      // (Founder/Project have none) — without this branch it fell through to the default TextField
      // case and drew a text box for a yes/no consent, which is not merely wrong styling: a text
      // input can't express `true`/`false` at all, so nothing typed into it would ever save
      // correctly. `Checkbox` takes its label as children rather than a `label` prop, so it can't
      // share `common` the way RadioGroup/Select do.
      case "CHECKBOX":
        return (
          <div className="flex flex-col gap-xs">
            <Checkbox
              isSelected={value === true}
              onChange={(checked) => {
                onChange(checked);
                onCommit(checked);
              }}
              isDisabled={isDisabled}
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-sm-medium text-(--text-secondary)">
                  {label}
                  {isRequired ? (
                    <span aria-hidden className="text-(--text-error)">
                      {" "}
                      *
                    </span>
                  ) : null}
                </span>
                {helpText ? (
                  <span className="text-sm-regular text-(--text-tertiary)">
                    {helpText}
                  </span>
                ) : null}
              </span>
            </Checkbox>
            {errorMessage ? (
              <p className="text-xs-regular text-(--text-error)">
                {errorMessage}
              </p>
            ) : null}
          </div>
        );

      // Practical's `availability_codes` is the first MULTI_SELECT field this dossier renders —
      // without this branch it fell through to the default TextField case exactly like CHECKBOX
      // did before its own branch existed, and a text input can no more hold several codes than
      // it could a boolean. `value` is an array on the wire; `MultiSelect` works in string ids,
      // so it round-trips through the same `keyOf`/`valueForKey` pair DROPDOWN and RADIO already
      // use to keep the original typed value intact.
      case "MULTI_SELECT": {
        const selectedKeys = Array.isArray(value)
          ? value.map((entry) => String(entry))
          : [];
        return (
          <MultiSelect
            {...common}
            options={options.map((option) => ({
              id: keyOf(option),
              label: option.label,
            }))}
            value={selectedKeys}
            placeholder={t("dossier.select-placeholder")}
            // Every toggle inside the popover is a complete choice, same as a radio or a
            // single-select dropdown — there is no separate "confirm" step for this control.
            onChange={(keys) => {
              const next = keys.map((key) => valueForKey(key));
              onChange(next);
              onCommit(next);
            }}
          />
        );
      }

      case "NUMBER":
        return (
          <NumberField
            {...common}
            // Only spread a `formatOptions` key at all when a caller actually asks for decimals —
            // `formatOptions={undefined}` would still WIN over NumberField's own zero-decimal
            // default (an explicit `undefined` key overrides a spread that comes before it), so
            // every existing whole-number field must never see this key in the first place.
            {...(maxFractionDigits === undefined
              ? {}
              : {
                  formatOptions: {
                    useGrouping: false,
                    maximumFractionDigits: maxFractionDigits,
                  },
                })}
            // The wire type is a NUMBER, not a numeric string — the server rejects the string form.
            // `NaN` is React Aria's empty state and must never be sent.
            value={typeof value === "number" ? value : Number.NaN}
            onChange={(next) => {
              onChange(Number.isNaN(next) ? null : next);
            }}
            onBlur={() => {
              const current = latestValue.current;
              onCommit(typeof current === "number" ? current : null);
            }}
          />
        );

      case "LONG_TEXT":
        return (
          <TextArea
            {...common}
            value={text}
            onChange={onChange}
            // Committed on BLUR rather than per keystroke. US-E3-06 wants what was typed saved
            // "shortly after they stop", and for a several-hundred-word answer over 3G, one request
            // when the field is done beats one per pause.
            onBlur={() => onCommit(text)}
          />
        );

      default:
        return (
          <TextField
            {...common}
            type={inputType === "URL" ? "url" : "text"}
            value={text}
            onChange={onChange}
            onBlur={() => onCommit(text)}
          />
        );
    }
  })();

  return (
    <div className="flex flex-col gap-xs">
      {control}
      {warnings.map((warning) => (
        <FieldWarning key={warning.code} warning={warning} />
      ))}
    </div>
  );
}
